import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
    Activity,
    AlertTriangle,
    Car,
    ExternalLink,
    Headset,
    LayoutGrid,
    Loader2,
    MessageSquare,
    Plus,
    RefreshCw,
    Truck,
    Users,
    FileText,
    Gauge,
    Link2,
    Scale,
    FolderOpen,
    Wallet,
} from "lucide-react";
import { toast } from "sonner";
import { filoMerkez } from "@/lib/api";
import { Button } from "@/components/ui/button";
import FiloHasarDetayDrawer from "@/components/filo/FiloHasarDetayDrawer";

const TABS = [
    { id: "ozet", label: "Özet", icon: LayoutGrid },
    { id: "hasarlar", label: "Hasarlar", icon: AlertTriangle },
    { id: "filolar", label: "Filolar", icon: Truck },
    { id: "aktivite", label: "Aktivite", icon: Activity },
    { id: "sla", label: "SLA", icon: Scale },
    { id: "performans", label: "Performans", icon: Gauge },
    { id: "dosyalar", label: "Dosyalar", icon: FolderOpen },
    { id: "odemeler", label: "Ödemeler", icon: Wallet },
];

const HASAR_DURUMLARI = [
    { value: "", label: "Tümü" },
    { value: "BILDIRILDI", label: "İhbar Edildi" },
    { value: "EKSPER_BEKLIYOR", label: "Eksper Bekleniyor" },
    { value: "ONARIMDA", label: "Onarımda" },
    { value: "TAMAMLANDI", label: "Tamamlandı" },
    { value: "HUKUKTA", label: "Hukuki Süreç" },
];

const DURUM_RENK = {
    BILDIRILDI: "text-sky-300 bg-sky-500/10 border-sky-500/30",
    EKSPER_BEKLIYOR: "text-amber-300 bg-amber-500/10 border-amber-500/30",
    ONARIMDA: "text-orange-300 bg-orange-500/10 border-orange-500/30",
    TAMAMLANDI: "text-emerald-300 bg-emerald-500/10 border-emerald-500/30",
    HUKUKTA: "text-purple-300 bg-purple-500/10 border-purple-500/30",
};

function KpiCard({ label, value, hint, accent = "gold" }) {
    const ring =
        accent === "red"
            ? "border-rose-500/30 bg-rose-500/5"
            : accent === "amber"
              ? "border-amber-500/30 bg-amber-500/5"
              : "border-[#D4AF37]/25 bg-[#121A2F]/80";
    return (
        <div className={`rounded-xl border p-4 ${ring}`}>
            <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
            <div className="font-display text-2xl text-white mt-1">{value ?? "—"}</div>
            {hint ? <div className="text-[11px] text-slate-500 mt-1">{hint}</div> : null}
        </div>
    );
}

function Badge({ children, className = "" }) {
    return (
        <span
            className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold border ${className}`}
        >
            {children}
        </span>
    );
}

const CRUD_LINKS = [
    { to: "/e/filolar", label: "Filolar", icon: Truck },
    { to: "/e/filo-araclari", label: "Araçlar", icon: Car },
    { to: "/e/suruculer", label: "Sürücüler", icon: Users },
    { to: "/e/hasar-kayitlari", label: "Hasar Kayıtları", icon: AlertTriangle },
    { to: "/e/hasar-evraklari", label: "Hasar Evrakları", icon: FileText },
    { to: "/filo/canli-destek", label: "Canlı Destek", icon: Headset },
];

export default function FiloMerkezPage() {
    const [tab, setTab] = useState("ozet");
    const [loading, setLoading] = useState(true);
    const [busyKey, setBusyKey] = useState(null);
    const [ozet, setOzet] = useState(null);
    const [hasarlar, setHasarlar] = useState([]);
    const [filolar, setFilolar] = useState([]);
    const [aktiviteler, setAktiviteler] = useState([]);
    const [hasarFiltre, setHasarFiltre] = useState("");
    const [hukukFiltre, setHukukFiltre] = useState("");
    const [filoArama, setFiloArama] = useState("");
    const [geciken, setGeciken] = useState([]);
    const [performans, setPerformans] = useState([]);
    const [dosyalar, setDosyalar] = useState([]);
    const [odemeler, setOdemeler] = useState([]);
    const [dosyaArama, setDosyaArama] = useState("");
    const [odemeFiltre, setOdemeFiltre] = useState("");
    const [wizardHasar, setWizardHasar] = useState(null);
    const [adayDosyalar, setAdayDosyalar] = useState([]);
    const [detayHasarId, setDetayHasarId] = useState(null);
    const [performansOzet, setPerformansOzet] = useState(null);
    const [trendAylik, setTrendAylik] = useState([]);

    const loadOzet = useCallback(async () => {
        const data = await filoMerkez.ozet();
        setOzet(data);
    }, []);

    const loadHasarlar = useCallback(async () => {
        const params = { limit: 100 };
        if (hasarFiltre) params.durum = hasarFiltre;
        if (hukukFiltre) params.hukuk = hukukFiltre;
        const data = await filoMerkez.hasarlar(params);
        setHasarlar(data.hasarlar || []);
    }, [hasarFiltre, hukukFiltre]);

    const loadFilolar = useCallback(async () => {
        const params = { limit: 120 };
        if (filoArama.trim()) params.q = filoArama.trim();
        const data = await filoMerkez.filolar(params);
        setFilolar(data.filolar || []);
    }, [filoArama]);

    const loadAktivite = useCallback(async () => {
        const data = await filoMerkez.aktivite({ limit: 50 });
        setAktiviteler(data.aktiviteler || []);
    }, []);

    const loadSla = useCallback(async () => {
        const data = await filoMerkez.sla({ limit: 80 });
        setGeciken(data.geciken || []);
    }, []);

    const loadPerformans = useCallback(async () => {
        const data = await filoMerkez.performans();
        setPerformans(data.filolar || []);
        setPerformansOzet(data.ozet || null);
        setTrendAylik(data.trend_aylik || []);
    }, []);

    const loadDosyalar = useCallback(async () => {
        const params = { limit: 120 };
        if (dosyaArama.trim()) params.q = dosyaArama.trim();
        const data = await filoMerkez.dosyalar(params);
        setDosyalar(data.dosyalar || []);
    }, [dosyaArama]);

    const loadOdemeler = useCallback(async () => {
        const params = { limit: 120 };
        if (odemeFiltre) params.durum = odemeFiltre;
        const data = await filoMerkez.odemeler(params);
        setOdemeler(data.odemeler || []);
    }, [odemeFiltre]);

    const refreshAll = useCallback(async () => {
        setLoading(true);
        try {
            await Promise.all([
                loadOzet(),
                loadHasarlar(),
                loadFilolar(),
                loadAktivite(),
                loadSla(),
                loadPerformans(),
                loadDosyalar(),
                loadOdemeler(),
            ]);
        } catch {
            toast.error("Veriler yüklenemedi.");
        } finally {
            setLoading(false);
        }
    }, [loadOzet, loadHasarlar, loadFilolar, loadAktivite, loadSla, loadPerformans, loadDosyalar, loadOdemeler]);

    useEffect(() => {
        refreshAll();
    }, [refreshAll]);

    useEffect(() => {
        if (tab === "hasarlar") loadHasarlar().catch(() => {});
    }, [tab, hasarFiltre, hukukFiltre, loadHasarlar]);

    useEffect(() => {
        if (tab === "filolar") loadFilolar().catch(() => {});
    }, [tab, filoArama, loadFilolar]);

    useEffect(() => {
        if (tab === "sla") loadSla().catch(() => {});
    }, [tab, loadSla]);

    useEffect(() => {
        if (tab === "performans") loadPerformans().catch(() => {});
    }, [tab, loadPerformans]);

    useEffect(() => {
        if (tab === "dosyalar") loadDosyalar().catch(() => {});
    }, [tab, dosyaArama, loadDosyalar]);

    useEffect(() => {
        if (tab === "odemeler") loadOdemeler().catch(() => {});
    }, [tab, odemeFiltre, loadOdemeler]);

    async function openWizard(hasar) {
        setWizardHasar(hasar);
        try {
            const data = await filoMerkez.dosyaAday(hasar.id);
            setAdayDosyalar(data.adaylar || []);
        } catch {
            toast.error("Aday dosyalar yüklenemedi.");
        }
    }

    async function handleDosyaOlustur(hasarId) {
        setBusyKey(`dosya-${hasarId}`);
        try {
            await filoMerkez.hasarDosyaOlustur(hasarId);
            toast.success("Hukuk dosyası oluşturuldu.");
            await Promise.all([loadHasarlar(), loadOzet()]);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Oluşturulamadı.");
        } finally {
            setBusyKey(null);
        }
    }

    async function handleDosyaBagla(hasarId, dosyaId) {
        setBusyKey(`bagla-${hasarId}`);
        try {
            await filoMerkez.hasarDosyaBagla(hasarId, dosyaId);
            toast.success("Dosya bağlandı.");
            setWizardHasar(null);
            await Promise.all([loadHasarlar(), loadOzet()]);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Bağlanamadı.");
        } finally {
            setBusyKey(null);
        }
    }

    async function handleDurumDegistir(hasarId, durum) {
        setBusyKey(`durum-${hasarId}`);
        try {
            await filoMerkez.hasarDurum(hasarId, durum);
            toast.success("Durum güncellendi.");
            await Promise.all([loadHasarlar(), loadOzet()]);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Güncellenemedi.");
        } finally {
            setBusyKey(null);
        }
    }

    return (
        <div className="p-4 md:p-6 max-w-[1400px] mx-auto space-y-5">
            <header className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                <div>
                    <h1 className="font-display text-2xl text-white flex items-center gap-2">
                        <Truck className="h-7 w-7 text-[#D4AF37]" />
                        Filo Merkezi
                    </h1>
                    <p className="text-sm text-slate-500 mt-1 max-w-2xl">
                        Kurumsal filo hesapları, hasar ihbarları, araç envanteri ve canlı destek —
                        tek operasyon ekranından tam CRUD yönetimi.
                    </p>
                </div>
                <div className="flex flex-wrap gap-2">
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="border-[#D4AF37]/30 text-[#D4AF37]"
                        onClick={refreshAll}
                        disabled={loading}
                    >
                        {loading ? (
                            <Loader2 className="h-4 w-4 animate-spin mr-1" />
                        ) : (
                            <RefreshCw className="h-4 w-4 mr-1" />
                        )}
                        Yenile
                    </Button>
                    <a
                        href={ozet?.rapor_pdf_link || "https://mbshukuk.com/filo/panel/rapor-aylik/"}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-xs px-3 py-2 rounded-md border border-white/10 text-slate-300 hover:border-[#8b1f2f]/40"
                    >
                        <FileText className="h-3.5 w-3.5" /> Aylık PDF
                    </a>
                    <a
                        href={ozet?.portal_link || "https://mbshukuk.com/filo/giris/"}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-xs px-3 py-2 rounded-md border border-white/10 text-slate-300 hover:border-[#8b1f2f]/40"
                    >
                        <ExternalLink className="h-3.5 w-3.5" /> Filo portalı
                    </a>
                    <Link
                        to="/e/filolar?new=1"
                        className="inline-flex items-center gap-1 text-xs px-3 py-2 rounded-md bg-[#8b1f2f]/80 text-white hover:bg-[#8b1f2f]"
                    >
                        <Plus className="h-3.5 w-3.5" /> Yeni filo
                    </Link>
                </div>
            </header>

            {ozet ? (
                <div className="grid grid-cols-2 lg:grid-cols-4 xl:grid-cols-10 gap-3">
                    <KpiCard label="Aktif filo" value={ozet.aktif_filo} />
                    <KpiCard label="Toplam araç" value={ozet.toplam_arac} hint={`${ozet.aktif_arac} aktif`} />
                    <KpiCard label="Hasar kaydı" value={ozet.toplam_hasar} accent="amber" />
                    <KpiCard label="Bugün ihbar" value={ozet.hasar_bugun} />
                    <KpiCard
                        label="Hukuk bekleyen"
                        value={ozet.hukuk_bekleyen_hasar}
                        accent="red"
                        hint="Dosyası bağlanmamış"
                    />
                    <KpiCard label="Açık dosya" value={ozet.dosya_acik} />
                    <KpiCard label="Tahkim" value={ozet.dosya_tahkim} />
                    <KpiCard label="Ödeme bekleyen" value={ozet.odeme_bekleyen} accent="amber" />
                    <KpiCard label="Ödeme toplam" value={`${Number(ozet.odeme_toplam || 0).toLocaleString("tr-TR")} ₺`} />
                    <KpiCard
                        label="Okunmamış chat"
                        value={ozet.chat_okunmamis}
                        accent={ozet.chat_okunmamis ? "amber" : "gold"}
                    />
                </div>
            ) : null}

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
                {CRUD_LINKS.map(({ to, label, icon: Icon }) => (
                    <Link
                        key={to}
                        to={to}
                        className="flex items-center gap-2 px-3 py-2.5 rounded-lg border border-white/10 bg-[#121A2F]/60 text-xs text-slate-300 hover:border-[#D4AF37]/30 hover:text-white transition-colors"
                    >
                        <Icon className="h-4 w-4 text-[#D4AF37]" />
                        {label}
                    </Link>
                ))}
            </div>

            <div className="flex flex-wrap gap-1 border-b border-white/10 pb-2">
                {TABS.map(({ id, label, icon: Icon }) => (
                    <button
                        key={id}
                        type="button"
                        onClick={() => setTab(id)}
                        className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-t-md text-xs font-medium transition-colors ${
                            tab === id
                                ? "bg-[#121A2F] text-[#D4AF37] border border-white/10 border-b-transparent -mb-px"
                                : "text-slate-500 hover:text-slate-300"
                        }`}
                    >
                        <Icon className="h-3.5 w-3.5" />
                        {label}
                    </button>
                ))}
            </div>

            {tab === "ozet" && ozet ? (
                <div className="grid lg:grid-cols-2 gap-4">
                    <section className="rounded-xl border border-white/10 bg-[#121A2F]/50 p-4">
                        <h2 className="text-sm font-semibold text-white mb-3">Hasar durum dağılımı</h2>
                        <div className="space-y-2">
                            {Object.entries(ozet.hasar_durum || {}).map(([k, v]) => (
                                <div key={k} className="flex justify-between text-sm">
                                    <span className="text-slate-400">
                                        {HASAR_DURUMLARI.find((d) => d.value === k)?.label || k}
                                    </span>
                                    <span className="text-white font-medium">{v}</span>
                                </div>
                            ))}
                            {!Object.keys(ozet.hasar_durum || {}).length ? (
                                <p className="text-sm text-slate-500">Henüz hasar kaydı yok.</p>
                            ) : null}
                        </div>
                    </section>
                    <section className="rounded-xl border border-white/10 bg-[#121A2F]/50 p-4">
                        <h2 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                            <MessageSquare className="h-4 w-4 text-[#D4AF37]" />
                            Hızlı erişim
                        </h2>
                        <ul className="space-y-2 text-sm text-slate-400">
                            <li>
                                <Link to="/filo/canli-destek" className="text-[#D4AF37] hover:underline">
                                    Canlı destek chat
                                </Link>
                                {ozet.chat_okunmamis > 0
                                    ? ` — ${ozet.chat_okunmamis} okunmamış mesaj`
                                    : ""}
                            </li>
                            <li>
                                <Link
                                    to="/e/hasar-kayitlari"
                                    className="text-[#D4AF37] hover:underline"
                                >
                                    Tüm hasar kayıtları (CRUD)
                                </Link>
                            </li>
                            <li>
                                Son 7 gün: <strong className="text-white">{ozet.hasar_7_gun}</strong> yeni
                                ihbar
                            </li>
                            <li>
                                Pasif filo: <strong className="text-white">{ozet.pasif_filo}</strong>
                            </li>
                        </ul>
                    </section>
                </div>
            ) : null}

            {tab === "hasarlar" ? (
                <section className="space-y-3">
                    <div className="flex flex-wrap gap-2 items-center">
                        <select
                            value={hasarFiltre}
                            onChange={(e) => setHasarFiltre(e.target.value)}
                            className="text-xs rounded-md border border-white/10 bg-[#0B1220] text-slate-300 px-2 py-1.5"
                        >
                            {HASAR_DURUMLARI.map((d) => (
                                <option key={d.value || "all"} value={d.value}>
                                    {d.label}
                                </option>
                            ))}
                        </select>
                        <select
                            value={hukukFiltre}
                            onChange={(e) => setHukukFiltre(e.target.value)}
                            className="text-xs rounded-md border border-white/10 bg-[#0B1220] text-slate-300 px-2 py-1.5"
                        >
                            <option value="">Hukuk — tümü</option>
                            <option value="bekleyen">Dosya bekleyen</option>
                            <option value="bagli">Dosyaya bağlı</option>
                        </select>
                        <Link
                            to="/e/hasar-kayitlari?new=1"
                            className="ml-auto text-xs text-[#D4AF37] hover:underline"
                        >
                            + Yeni hasar kaydı
                        </Link>
                    </div>
                    <div className="rounded-xl border border-white/10 overflow-hidden">
                        <table className="w-full text-sm">
                            <thead className="bg-[#0B1220] text-[10px] uppercase text-slate-500">
                                <tr>
                                    <th className="text-left p-3">Plaka / Filo</th>
                                    <th className="text-left p-3">Kaza</th>
                                    <th className="text-left p-3">Durum</th>
                                    <th className="text-left p-3">Hukuk dosyası</th>
                                    <th className="text-right p-3">İşlem</th>
                                </tr>
                            </thead>
                            <tbody>
                                {hasarlar.map((h) => (
                                    <tr key={h.id} className="border-t border-white/5 hover:bg-white/[0.02]">
                                        <td className="p-3">
                                            <button
                                                type="button"
                                                onClick={() => setDetayHasarId(h.id)}
                                                className="font-medium text-white hover:text-[#D4AF37] text-left"
                                            >
                                                {h.plaka}
                                            </button>
                                            <div className="text-[11px] text-slate-500">{h.filo_adi}</div>
                                        </td>
                                        <td className="p-3 text-slate-400 text-xs">
                                            {h.hasar_tipi_label}
                                            <div>{h.kaza_konumu || "—"}</div>
                                        </td>
                                        <td className="p-3">
                                            <Badge className={DURUM_RENK[h.durum] || ""}>
                                                {h.durum_label}
                                            </Badge>
                                        </td>
                                        <td className="p-3">
                                            {h.dosya_no ? (
                                                <Link
                                                    to={h.dosya_link}
                                                    className="text-xs text-[#D4AF37] hover:underline"
                                                >
                                                    {h.dosya_no}
                                                </Link>
                                            ) : (
                                                <span className="text-xs text-rose-400">Bağlı değil</span>
                                            )}
                                        </td>
                                        <td className="p-3 text-right space-y-1">
                                            <select
                                                value={h.durum}
                                                disabled={busyKey === `durum-${h.id}`}
                                                onChange={(e) =>
                                                    handleDurumDegistir(h.id, e.target.value)
                                                }
                                                className="text-[10px] rounded border border-white/10 bg-[#0B1220] text-slate-300 px-1 py-0.5 max-w-[120px] block ml-auto"
                                            >
                                                {HASAR_DURUMLARI.filter((d) => d.value).map((d) => (
                                                    <option key={d.value} value={d.value}>
                                                        {d.label}
                                                    </option>
                                                ))}
                                            </select>
                                            {!h.dosya_no ? (
                                                <div className="flex gap-1 justify-end flex-wrap">
                                                    <button
                                                        type="button"
                                                        className="text-[10px] text-emerald-400 hover:underline"
                                                        disabled={busyKey === `dosya-${h.id}`}
                                                        onClick={() => handleDosyaOlustur(h.id)}
                                                    >
                                                        Dosya aç
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="text-[10px] text-[#D4AF37] hover:underline"
                                                        onClick={() => openWizard(h)}
                                                    >
                                                        Bağla
                                                    </button>
                                                </div>
                                            ) : null}
                                        </td>
                                    </tr>
                                ))}
                                {!hasarlar.length ? (
                                    <tr>
                                        <td colSpan={5} className="p-8 text-center text-slate-500">
                                            Kayıt bulunamadı.
                                        </td>
                                    </tr>
                                ) : null}
                            </tbody>
                        </table>
                    </div>
                </section>
            ) : null}

            {tab === "filolar" ? (
                <section className="space-y-3">
                    <input
                        type="search"
                        placeholder="Firma, e-posta veya vergi no ara…"
                        value={filoArama}
                        onChange={(e) => setFiloArama(e.target.value)}
                        className="w-full max-w-md text-sm rounded-lg border border-white/10 bg-[#0B1220] text-slate-300 px-3 py-2"
                    />
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        {filolar.map((f) => (
                            <article
                                key={f.id}
                                className="rounded-xl border border-white/10 bg-[#121A2F]/50 p-4 hover:border-[#D4AF37]/25 transition-colors"
                            >
                                <div className="flex items-start justify-between gap-2">
                                    <Link
                                        to={f.filo_link}
                                        className="font-medium text-white hover:text-[#D4AF37] line-clamp-2"
                                    >
                                        {f.firma_adi}
                                    </Link>
                                    <Badge
                                        className={
                                            f.aktif
                                                ? "text-emerald-300 bg-emerald-500/10 border-emerald-500/30"
                                                : "text-slate-400 bg-white/5 border-white/10"
                                        }
                                    >
                                        {f.aktif ? "Aktif" : "Pasif"}
                                    </Badge>
                                </div>
                                <p className="text-[11px] text-slate-500 mt-1">{f.email}</p>
                                <p className="text-[11px] text-slate-500">{f.filo_tipi_label}</p>
                                <div className="flex gap-3 mt-3 text-xs text-slate-400">
                                    <span>{f.arac_sayisi} araç</span>
                                    <span>{f.hasar_sayisi} hasar</span>
                                    <span>{f.dosya_sayisi} dosya</span>
                                </div>
                                <div className="flex gap-2 mt-3">
                                    <Link
                                        to={`/e/filo-araclari?filo=${f.id}`}
                                        className="text-[10px] text-[#D4AF37] hover:underline"
                                    >
                                        Araçlar
                                    </Link>
                                    <Link
                                        to={`/e/hasar-kayitlari?filo=${f.id}`}
                                        className="text-[10px] text-[#D4AF37] hover:underline"
                                    >
                                        Hasarlar
                                    </Link>
                                </div>
                            </article>
                        ))}
                        {!filolar.length ? (
                            <p className="text-sm text-slate-500 col-span-full text-center py-8">
                                Filo bulunamadı.
                            </p>
                        ) : null}
                    </div>
                </section>
            ) : null}

            {tab === "aktivite" ? (
                <section className="rounded-xl border border-white/10 divide-y divide-white/5">
                    {aktiviteler.map((a, i) => (
                        <Link
                            key={`${a.tip}-${a.link}-${i}`}
                            to={a.link}
                            className="flex items-center gap-3 p-3 hover:bg-white/[0.02]"
                        >
                            <span
                                className={`shrink-0 w-8 h-8 rounded-lg flex items-center justify-center ${
                                    a.tip === "hasar"
                                        ? "bg-amber-500/10 text-amber-400"
                                        : "bg-sky-500/10 text-sky-400"
                                }`}
                            >
                                {a.tip === "hasar" ? (
                                    <AlertTriangle className="h-4 w-4" />
                                ) : (
                                    <Car className="h-4 w-4" />
                                )}
                            </span>
                            <div className="min-w-0">
                                <div className="text-sm text-white truncate">{a.baslik}</div>
                                <div className="text-[11px] text-slate-500 truncate">{a.alt}</div>
                            </div>
                            <span className="ml-auto text-[10px] text-slate-600 shrink-0">
                                {a.tarih ? a.tarih.slice(0, 10) : ""}
                            </span>
                        </Link>
                    ))}
                    {!aktiviteler.length ? (
                        <p className="p-8 text-center text-slate-500 text-sm">Aktivite yok.</p>
                    ) : null}
                </section>
            ) : null}

            {tab === "sla" ? (
                <section className="rounded-xl border border-white/10 overflow-hidden">
                    <table className="w-full text-sm">
                        <thead className="bg-[#0B1220] text-[10px] uppercase text-slate-500">
                            <tr>
                                <th className="text-left p-3">Plaka</th>
                                <th className="text-left p-3">Filo</th>
                                <th className="text-left p-3">Aşama</th>
                                <th className="text-left p-3">Geçen saat</th>
                            </tr>
                        </thead>
                        <tbody>
                            {geciken.map((g) => (
                                <tr key={g.hasar_id} className="border-t border-white/5">
                                    <td className="p-3 text-white">{g.plaka}</td>
                                    <td className="p-3 text-slate-400">{g.filo}</td>
                                    <td className="p-3">{g.mevcut_label}</td>
                                    <td className="p-3 text-rose-400">{g.gecen_saat}h / {g.hedef_saat}h</td>
                                </tr>
                            ))}
                            {!geciken.length ? (
                                <tr>
                                    <td colSpan={4} className="p-8 text-center text-slate-500">
                                        SLA gecikmesi yok.
                                    </td>
                                </tr>
                            ) : null}
                        </tbody>
                    </table>
                </section>
            ) : null}

            {tab === "performans" ? (
                <section className="space-y-4">
                    {performansOzet ? (
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                            <KpiCard
                                label="SLA ihlal (toplam)"
                                value={performansOzet.sla_ihlal_toplam}
                                accent="red"
                            />
                            <KpiCard label="Toplam hasar" value={performansOzet.toplam_hasar} />
                            <KpiCard label="Aktif filo" value={performansOzet.aktif_filo} />
                        </div>
                    ) : null}
                    {trendAylik.length ? (
                        <div className="rounded-xl border border-white/10 p-4">
                            <div className="text-[10px] uppercase text-slate-500 mb-2">
                                Son 6 ay hasar trendi
                            </div>
                            <div className="flex items-end gap-2 h-24">
                                {trendAylik.map((t) => (
                                    <div key={t.ay} className="flex-1 flex flex-col items-center gap-1">
                                        <div
                                            className="w-full bg-[#D4AF37]/30 rounded-t"
                                            style={{
                                                height: `${Math.max(8, Math.min(100, t.adet * 12))}px`,
                                            }}
                                            title={`${t.ay}: ${t.adet}`}
                                        />
                                        <span className="text-[9px] text-slate-600">{t.ay.slice(0, 3)}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ) : null}
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        {performans.map((p) => (
                            <article
                                key={p.filo_id}
                                className="rounded-xl border border-white/10 bg-[#121A2F]/50 p-4"
                            >
                                <div className="text-sm text-white font-medium">{p.firma_adi}</div>
                                <div className="font-display text-3xl text-[#D4AF37] mt-2">{p.skor ?? "—"}</div>
                                <div className="text-[11px] text-slate-500 mt-1 space-y-0.5">
                                    <div>
                                        Evrak %{p.evrak_ort ?? "—"} · SLA %{p.sla_ort ?? "—"} ·{" "}
                                        {p.hasar_sayisi} hasar
                                    </div>
                                    {p.ortalama_onarim_gun != null ? (
                                        <div>Ort. onarım: {p.ortalama_onarim_gun} gün</div>
                                    ) : null}
                                    {p.ekspertiz_dosya_saat != null ? (
                                        <div>Ekspertiz→dosya: {p.ekspertiz_dosya_saat} sa</div>
                                    ) : null}
                                    <div>
                                        Eksik evrak: %{p.eksik_evrak_orani ?? 0} · SLA ihlal:{" "}
                                        {p.sla_ihlal ?? 0}
                                    </div>
                                </div>
                                <Link
                                    to={`/e/filolar/${p.filo_id}`}
                                    className="text-[10px] text-[#D4AF37] hover:underline mt-2 inline-block"
                                >
                                    Filo detay
                                </Link>
                            </article>
                        ))}
                    </div>
                </section>
            ) : null}

            {tab === "dosyalar" ? (
                <section className="space-y-3">
                    <div className="flex flex-wrap gap-2 items-center">
                        <input
                            type="search"
                            value={dosyaArama}
                            onChange={(e) => setDosyaArama(e.target.value)}
                            placeholder="Dosya no, plaka, sigorta…"
                            className="text-xs rounded-md border border-white/10 bg-[#0B1220] text-slate-300 px-3 py-1.5 min-w-[220px]"
                        />
                        <Link
                            to="/e/sigorta-dosyalari"
                            className="ml-auto text-xs text-[#D4AF37] hover:underline"
                        >
                            Tüm sigorta dosyaları
                        </Link>
                    </div>
                    <div className="rounded-xl border border-white/10 overflow-hidden">
                        <table className="w-full text-sm">
                            <thead className="bg-[#0B1220] text-[10px] uppercase text-slate-500">
                                <tr>
                                    <th className="text-left p-3">Dosya</th>
                                    <th className="text-left p-3">Filo</th>
                                    <th className="text-left p-3">Plaka</th>
                                    <th className="text-left p-3">Sigorta</th>
                                    <th className="text-left p-3">Durum</th>
                                </tr>
                            </thead>
                            <tbody>
                                {dosyalar.map((d) => (
                                    <tr key={d.id} className="border-t border-white/5 hover:bg-white/[0.02]">
                                        <td className="p-3">
                                            <Link
                                                to={d.link}
                                                className="font-medium text-white hover:text-[#D4AF37]"
                                            >
                                                {d.dosya_no || "—"}
                                            </Link>
                                            <div className="text-[11px] text-slate-500">{d.muvekkil}</div>
                                        </td>
                                        <td className="p-3 text-slate-400 text-xs">{d.filo_adi}</td>
                                        <td className="p-3 text-slate-300">{d.plaka}</td>
                                        <td className="p-3 text-slate-400 text-xs">{d.sigorta_sirketi || "—"}</td>
                                        <td className="p-3">{d.durum_label}</td>
                                    </tr>
                                ))}
                                {!dosyalar.length ? (
                                    <tr>
                                        <td colSpan={5} className="p-8 text-center text-slate-500">
                                            Filo dosyası bulunamadı.
                                        </td>
                                    </tr>
                                ) : null}
                            </tbody>
                        </table>
                    </div>
                </section>
            ) : null}

            {tab === "odemeler" ? (
                <section className="space-y-3">
                    <div className="flex flex-wrap gap-2 items-center">
                        <select
                            value={odemeFiltre}
                            onChange={(e) => setOdemeFiltre(e.target.value)}
                            className="text-xs rounded-md border border-white/10 bg-[#0B1220] text-slate-300 px-2 py-1.5"
                        >
                            <option value="">Tüm ödemeler</option>
                            <option value="bekleyen">Bekleyen</option>
                            <option value="yapildi">Tamamlanan</option>
                        </select>
                        <Link
                            to="/e/odemeler"
                            className="ml-auto text-xs text-[#D4AF37] hover:underline"
                        >
                            Ödeme CRUD
                        </Link>
                    </div>
                    <div className="rounded-xl border border-white/10 overflow-hidden">
                        <table className="w-full text-sm">
                            <thead className="bg-[#0B1220] text-[10px] uppercase text-slate-500">
                                <tr>
                                    <th className="text-left p-3">Dosya</th>
                                    <th className="text-left p-3">Filo</th>
                                    <th className="text-left p-3">Tutar</th>
                                    <th className="text-left p-3">Sigortalı</th>
                                    <th className="text-left p-3">MBS</th>
                                    <th className="text-left p-3">Durum</th>
                                </tr>
                            </thead>
                            <tbody>
                                {odemeler.map((o) => (
                                    <tr key={o.id} className="border-t border-white/5 hover:bg-white/[0.02]">
                                        <td className="p-3">
                                            <Link
                                                to={o.dosya_link}
                                                className="font-medium text-white hover:text-[#D4AF37]"
                                            >
                                                {o.dosya_no || "—"}
                                            </Link>
                                            <div className="text-[11px] text-slate-500">{o.plaka}</div>
                                        </td>
                                        <td className="p-3 text-slate-400 text-xs">{o.filo_adi}</td>
                                        <td className="p-3 text-slate-300">
                                            {Number(o.odeme_miktari || 0).toLocaleString("tr-TR")} ₺
                                        </td>
                                        <td className="p-3 text-slate-400 text-xs">
                                            {Number(o.sigortali_hakedis || 0).toLocaleString("tr-TR")} ₺
                                        </td>
                                        <td className="p-3 text-slate-400 text-xs">
                                            {Number(o.mbs_hakedis || 0).toLocaleString("tr-TR")} ₺
                                        </td>
                                        <td className="p-3">
                                            {o.odeme_yapildi ? (
                                                <Badge className="text-emerald-300 bg-emerald-500/10 border-emerald-500/30">
                                                    Tamamlandı
                                                </Badge>
                                            ) : (
                                                <Badge className="text-amber-300 bg-amber-500/10 border-amber-500/30">
                                                    Bekliyor
                                                </Badge>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                                {!odemeler.length ? (
                                    <tr>
                                        <td colSpan={6} className="p-8 text-center text-slate-500">
                                            Ödeme kaydı yok.
                                        </td>
                                    </tr>
                                ) : null}
                            </tbody>
                        </table>
                    </div>
                </section>
            ) : null}

            {wizardHasar ? (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
                    <div className="w-full max-w-md rounded-xl border border-white/10 bg-[#121A2F] p-4 space-y-3">
                        <h3 className="text-white font-medium flex items-center gap-2">
                            <Link2 className="h-4 w-4 text-[#D4AF37]" />
                            Dosya bağlama — {wizardHasar.plaka}
                        </h3>
                        <ul className="max-h-60 overflow-y-auto space-y-2">
                            {adayDosyalar.map((d) => (
                                <li
                                    key={d.id}
                                    className="flex items-center justify-between gap-2 text-sm border border-white/5 rounded p-2"
                                >
                                    <span className="text-slate-300">
                                        {d.dosya_no} · {d.plaka}
                                    </span>
                                    <button
                                        type="button"
                                        className="text-[10px] text-[#D4AF37]"
                                        disabled={busyKey === `bagla-${wizardHasar.id}`}
                                        onClick={() => handleDosyaBagla(wizardHasar.id, d.id)}
                                    >
                                        Seç
                                    </button>
                                </li>
                            ))}
                            {!adayDosyalar.length ? (
                                <li className="text-slate-500 text-sm">Aday dosya yok — Dosya aç kullanın.</li>
                            ) : null}
                        </ul>
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="w-full"
                            onClick={() => setWizardHasar(null)}
                        >
                            Kapat
                        </Button>
                    </div>
                </div>
            ) : null}

            <FiloHasarDetayDrawer
                hasarId={detayHasarId}
                onClose={() => setDetayHasarId(null)}
                onRefresh={loadHasarlar}
            />
        </div>
    );
}
